/**
 * Map over `items`, running at most `limit` invocations of `fn` at once, and
 * return the results in input order. A fixed pool of workers pulls from a shared
 * cursor, so memory/network pressure stays bounded regardless of `items.length`.
 *
 * If `fn` rejects, this call rejects too (already-running work still settles, but
 * no further items are started). Callers that want partial success — e.g. marking
 * one failed upload without aborting the rest — should catch inside `fn` and
 * resolve a result object instead of throwing.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length);
  if (items.length === 0) return results;

  const workers = Math.max(1, Math.min(Math.floor(limit) || 1, items.length));
  let cursor = 0;

  async function run(): Promise<void> {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await fn(items[index], index);
    }
  }

  await Promise.all(Array.from({ length: workers }, () => run()));
  return results;
}

/** Split an array into consecutive chunks of at most `size`. */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  const step = Math.max(1, Math.floor(size) || 1);
  for (let i = 0; i < items.length; i += step) {
    out.push(items.slice(i, i + step));
  }
  return out;
}
